import { t } from "@lingui/macro";
import { motion } from "framer-motion";

// Well-known French companies, shown as wordmarks (we have no official logo
// assets and cannot ship third-party trademark files).
const companies: string[] = [
  "Nextilabs",
  "Doctolib",
  "BlaBlaCar",
  "OVHcloud",
  "Capgemini",
  "Decathlon",
  "Ubisoft",
  "Deezer",
  "Qonto",
];

// Seamless auto-scroll for the mobile strip (list is rendered twice, so a
// -50% translate loops without a seam).
const LOGO_MARQUEE_STYLES = `
@keyframes izi-logo-marquee { to { transform: translateX(-50%); } }
.izi-logo-marquee { animation: izi-logo-marquee 20s linear infinite; }
@media (prefers-reduced-motion: reduce) { .izi-logo-marquee { animation: none; } }
`;

const Wordmark = ({ name }: { name: string }) => (
  <span className="whitespace-nowrap text-xl font-semibold tracking-tight text-neutral-500 opacity-70 transition-opacity duration-300 hover:opacity-100 dark:text-neutral-400 sm:text-2xl">
    {name}
  </span>
);

export const LogoCloudSection = () => (
  <section
    id="logo-cloud"
    className="relative bg-gradient-to-b from-transparent to-neutral-50/50 pb-16 pt-24 dark:to-neutral-900/30"
  >
    <style>{LOGO_MARQUEE_STYLES}</style>

    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }}
        className="text-center text-lg leading-relaxed text-neutral-600 dark:text-neutral-400"
      >
        {t`Izicvs is inspired by the resumes of candidates hired at leading French companies.`}
      </motion.p>

      {/* Seamless auto-scrolling marquee (all sizes), pauses on hover */}
      <div className="group relative mt-14 overflow-hidden">
        <div className="izi-logo-marquee flex w-max items-center gap-x-12 group-hover:[animation-play-state:paused] sm:gap-x-20">
          {[...companies, ...companies].map((company, index) => (
            <Wordmark key={`${company}-${index}`} name={company} />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent" />
      </div>

      {/* Subtle decorative top divider */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent opacity-50 dark:via-neutral-800"
      />
    </div>
  </section>
);
